/* pdp-zoom — PDP image lightbox with slide navigation and pan/pinch zoom.
 *
 * Reads its slides from whatever the gallery rendered: every `.velara-gallery__item[data-zoom-src]`
 * on the page, in DOM order. That is why it works for all four gallery variants without knowing
 * which one is on the page — `pdp-media-items` is the single renderer behind all of them.
 *
 * No dependency on jQuery/Bootstrap/EasyZoom (the reference's stack); the theme ships none of them.
 */
(function () {
  'use strict';

  var dialog = document.querySelector('[data-pdp-zoom]');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  var stage = dialog.querySelector('[data-zoom-stage]');
  var img = dialog.querySelector('[data-zoom-img]');
  var frame = dialog.querySelector('[data-zoom-frame]');
  var ghost = dialog.querySelector('[data-zoom-ghost]');
  var thumbsEl = dialog.querySelector('[data-zoom-thumbs]');
  var dotsEl = dialog.querySelector('[data-zoom-dots]');
  var countEl = dialog.querySelector('[data-zoom-count]');
  var prevBtn = dialog.querySelector('[data-zoom-prev]');
  var nextBtn = dialog.querySelector('[data-zoom-next]');

  var slides = [].slice.call(document.querySelectorAll('.velara-gallery__item[data-zoom-src]'));
  if (!slides.length) return;

  var index = 0;
  var scale = 1;
  var tx = 0;
  var ty = 0;
  var opener = null;
  var MAX = 2.5;
  var SWIPE = 40;   // px on the dominant axis; no time limit, a slow drag is still a swipe
  var SLIDE = 28;   // px of travel on the transition — a nudge, not a full carousel throw
  var DUR = 260;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  // The gallery this zoom opens from is a VERTICAL carousel on mobile and horizontal on desktop,
  // so the transition travels on whichever axis the swipe uses at that width.
  var vertical = window.matchMedia('(max-width: 767px)');

  function slideIn(dir) {
    if (reduced.matches || !dir) return;
    var axis = vertical.matches ? 'Y' : 'X';
    var from = dir > 0 ? SLIDE : -SLIDE;      // forward enters from the far side
    ghost.src = img.currentSrc || img.src;
    if (ghost.src) {
      ghost.style.transition = 'none';
      ghost.style.opacity = '1';
      ghost.style.transform = 'none';
      ghost.getBoundingClientRect();          // flush, or the browser coalesces both states
      ghost.style.transition = 'opacity ' + DUR + 'ms ease, transform ' + DUR + 'ms ease';
      ghost.style.opacity = '0';
      ghost.style.transform = 'translate' + axis + '(' + (-from) + 'px)';
    }
    frame.style.transition = 'none';
    frame.style.opacity = '0';
    frame.style.transform = 'translate' + axis + '(' + from + 'px)';
    frame.getBoundingClientRect();
    frame.style.transition = 'opacity ' + DUR + 'ms ease, transform ' + DUR + 'ms ease';
    frame.style.opacity = '1';
    frame.style.transform = 'none';
  }

  /* ---------- preloading ----------
   * The full-resolution renders are small (45-190KB here) but each one is a cold round trip, so an
   * un-preloaded slide change measured 530-680ms — all of it latency, none of it decode. Warming
   * the neighbours makes prev/next instant; the rest follow when the browser is idle. */
  var cache = new Array(slides.length);

  function preload(i) {
    i = (i + slides.length) % slides.length;
    if (cache[i]) return;
    var im = new Image();
    im.decoding = 'async';
    im.src = slides[i].getAttribute('data-zoom-src');
    cache[i] = im;
  }

  function preloadRest() {
    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 300); };
    idle(function () { for (var i = 0; i < slides.length; i++) preload(i); });
  }

  /* ---------- transform ---------- */

  function apply(animate) {
    img.style.transition = animate ? 'transform .22s ease' : 'none';
    img.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
    stage.classList.toggle('is-zoomed', scale > 1);
  }

  // Keep the image inside the stage: at scale 1 there is nothing to pan, and beyond it the
  // travel is half the overflow in each axis. Without the clamp a drag throws the photograph
  // off-screen and there is no way back except closing.
  function clamp() {
    if (scale <= 1) { tx = 0; ty = 0; return; }
    var r = img.getBoundingClientRect();
    var s = stage.getBoundingClientRect();
    var maxX = Math.max(0, (r.width - s.width) / 2);
    var maxY = Math.max(0, (r.height - s.height) / 2);
    tx = Math.min(maxX, Math.max(-maxX, tx));
    ty = Math.min(maxY, Math.max(-maxY, ty));
  }

  function resetZoom() { scale = 1; tx = 0; ty = 0; apply(false); }

  /* ---------- slides ---------- */

  function show(i, animate, dir) {
    var from = index;
    index = (i + slides.length) % slides.length;
    if (animate && dir === undefined && from !== index) {
      // Wrapping counts as one step, so 6 -> 1 travels forward rather than rewinding the gallery.
      var fwd = (index - from + slides.length) % slides.length;
      dir = fwd <= slides.length / 2 ? 1 : -1;
    }
    if (animate) slideIn(dir);
    var el = slides[index];
    img.src = el.getAttribute('data-zoom-src');
    img.alt = el.getAttribute('data-zoom-alt') || '';
    resetZoom();
    if (countEl) countEl.textContent = (index + 1) + ' / ' + slides.length;
    [].forEach.call(thumbsEl.children, function (t, n) {
      var on = n === index;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
      if (on && animate && t.scrollIntoView) t.scrollIntoView({ block: 'nearest', inline: 'center' });
    });
    [].forEach.call(dotsEl.children, function (d, n) {
      d.classList.toggle('is-active', n === index);
    });
    // Warm both neighbours, so a swipe or an arrow in either direction is already in cache.
    preload(index + 1);
    preload(index - 1);
    var single = slides.length < 2;
    prevBtn.hidden = single;
    nextBtn.hidden = single;
    thumbsEl.hidden = single;
    dotsEl.hidden = single;
  }

  function step(d) { show(index + d, true, d); }

  /* ---------- thumbnails ---------- */

  slides.forEach(function (el, i) {
    var src = el.getAttribute('data-zoom-src');
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'velara-zoom__thumb';
    b.setAttribute('role', 'tab');
    b.setAttribute('aria-label', String(i + 1));
    // The gallery already downloaded a small render of this image; reuse the <img> the gallery
    // painted rather than the 2048 one, so opening the dialog costs one request, not N.
    var inGallery = el.querySelector('img');
    var t = document.createElement('img');
    t.src = (inGallery && (inGallery.currentSrc || inGallery.src)) || src;
    t.alt = '';
    t.loading = 'lazy';
    b.appendChild(t);
    b.addEventListener('click', function () { show(i, true); });
    thumbsEl.appendChild(b);

    var dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'velara-zoom__dot';
    dot.tabIndex = -1;              // aria-hidden pager; the thumb rail is the accessible control
    dot.addEventListener('click', function () { show(i, true); });
    dotsEl.appendChild(dot);
  });

  /* ---------- open / close ---------- */

  function open(i, from) {
    opener = from || null;
    show(i, false);
    dialog.showModal();
    if (dialog.focus) dialog.focus();
    document.documentElement.classList.add('velara-zoom-open');
    preloadRest();
  }

  function close() {
    if (dialog.open) dialog.close();
  }

  dialog.addEventListener('close', function () {
    document.documentElement.classList.remove('velara-zoom-open');
    resetZoom();
    // Return focus where it came from, or the dialog traps the user at the top of the document.
    if (opener && opener.focus) opener.focus();
    opener = null;
  });

  dialog.querySelector('[data-zoom-close]').addEventListener('click', close);

  // Backdrop click. `::backdrop` is not an element, so the click lands on the dialog itself —
  // anything inside the stage or the rails must not close it.
  dialog.addEventListener('click', function (e) {
    if (e.target === dialog) close();
  });

  prevBtn.addEventListener('click', function () { step(-1); });
  nextBtn.addEventListener('click', function () { step(1); });

  dialog.addEventListener('keydown', function (e) {
    if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
  });

  /* ---------- triggers ---------- */

  slides.forEach(function (el, i) {
    el.classList.add('velara-gallery__item--zoomable');
    var target = el.querySelector('img') || el;
    target.addEventListener('pointerenter', function () { preload(i); }, { once: true });
    target.addEventListener('click', function (e) {
      e.preventDefault();
      open(i, target);
    });
    // Keyboard parity: the gallery item is not a control, so give it a role and a key handler
    // rather than leaving zoom mouse-only.
    el.tabIndex = 0;
    el.setAttribute('role', 'button');
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(i, el); }
    });
  });

  /* ---------- zoom + pan ---------- */

  stage.addEventListener('dblclick', function (e) {
    if (scale > 1) { resetZoom(); return; }
    var r = img.getBoundingClientRect();
    scale = 2;
    // Zoom toward the point that was double-clicked rather than the centre.
    tx = (r.left + r.width / 2 - e.clientX) * (scale - 1);
    ty = (r.top + r.height / 2 - e.clientY) * (scale - 1);
    clamp();
    apply(true);
  });

  var pts = new Map();
  var startDist = 0, startScale = 1, startX = 0, startY = 0, baseX = 0, baseY = 0, swipeFrom = null;

  function dist() {
    var v = [].slice.call(pts.values());
    return Math.hypot(v[0].x - v[1].x, v[0].y - v[1].y);
  }

  stage.addEventListener('pointerdown', function (e) {
    if (e.target.closest && e.target.closest('.velara-zoom__nav')) return;
    pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    // Guarded: setPointerCapture throws NotFoundError if the id is not an active pointer, and an
    // exception here aborts the handler BEFORE swipeFrom is set — killing the whole gesture.
    // Capture is an optimisation (it keeps a drag alive past the element edge), not a requirement.
    try { stage.setPointerCapture(e.pointerId); } catch (err) {}
    if (pts.size === 2) {
      startDist = dist();
      startScale = scale;
      swipeFrom = null;
    } else {
      startX = e.clientX; startY = e.clientY; baseX = tx; baseY = ty;
      // A drag at rest is a SLIDE; once zoomed the same gesture is a PAN, because panning is the
      // only way to reach the edges of a magnified photograph.
      swipeFrom = scale > 1 ? null : { x: e.clientX, y: e.clientY };
    }
  });

  stage.addEventListener('pointermove', function (e) {
    if (!pts.has(e.pointerId)) return;
    pts.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pts.size === 2 && startDist) {
      scale = Math.min(MAX, Math.max(1, startScale * (dist() / startDist)));
      clamp();
      apply(false);
      return;
    }
    if (scale > 1) {
      tx = baseX + (e.clientX - startX);
      ty = baseY + (e.clientY - startY);
      clamp();
      apply(false);
    }
  });

  function release(e) {
    pts.delete(e.pointerId);
    if (pts.size < 2) startDist = 0;

    if (swipeFrom) {
      var dx = e.clientX - swipeFrom.x;
      var dy = e.clientY - swipeFrom.y;
      var ax = Math.abs(dx), ay = Math.abs(dy);
      // BOTH axes navigate, whichever dominates. nove25.net's mobile gallery is a VERTICAL
      // carousel — swipe up/down browses, with the dots stacked on the right — so up/down has to
      // work here or the zoom contradicts the gallery it opened from. Left and up go forward.
      // Requiring one axis to beat the other by 1.2x keeps a diagonal drag from firing at random.
      if (ax > ay * 1.2 && ax > SWIPE) step(dx < 0 ? 1 : -1);
      else if (ay > ax * 1.2 && ay > SWIPE) step(dy < 0 ? 1 : -1);
      swipeFrom = null;
    }
    if (scale <= 1) resetZoom();
  }

  stage.addEventListener('pointerup', release);
  stage.addEventListener('pointercancel', release);

  window.addEventListener('resize', function () { clamp(); apply(false); });
})();
